import React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import InfiniteScroll from "react-infinite-scroll-component";

import { Button, Checkbox, Typography } from "@material-ui/core";

import ContentStudioMovementsStore from "stores/ContentStudioMovementsStore";

import Row from "components/Form/Row";
import Search from "components/Input/Search";
import Loader from "components/Layout/Loader";
import { SNACKBAR_TYPES } from "components/Snackbar";
import Placeholder from "components/Placeholder";
import SortingControl from "components/SortingControl";
import TrimmedText from "components/TrimmedText";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { DictionaryItem } from "constants/models";
import { TableSortType } from "stores/templates/TableStore";

import {
  Container,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  ScrollLoader,
  TitleContainer,
  SubtitleContainer,
  QuestionInfo,
  QuestionText,
  QuestionContainer,
  QuestionWrapper,
} from "./styles";
import QuestionStore from "../../../../../../../stores/QuestionStore";

type Props = {
  options: DictionaryItem[];
  onClose: () => void;
  onSave: (newMovement: DictionaryItem[]) => void;
} & SnackbarContextProps;

const FORM_INFORMATION_HEIGHT = "450px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class QuestionForm extends React.Component<Props> {
  @observable newQuestions: DictionaryItem[] = [];

  componentDidMount() {
    QuestionStore.fetch();
  }

  componentWillUnmount() {
    QuestionStore.resetPage();
    QuestionStore.clearSearch();
  }

  fetchMoreQuestions = async () => {
    const { setMessage } = this.props;

    try {
      await QuestionStore.fetchMoreQuestions();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onChangeCheckbox = (option: DictionaryItem) => () => {
    let items = this.newQuestions || [];

    if (items.find(item => item.value.id === option.value.id)) {
      items = items.filter(
        (item: DictionaryItem) => item.value.id !== option.value.id,
      );
    } else {
      items.push(option);
    }

    this.newQuestions = items;
  };

  onSaveNewMovement = () => {
    console.log(this.newQuestions);
    this.props.onSave(this.newQuestions);
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    QuestionStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && QuestionStore.fetch();
  };

  onSearchClear = () => {
    QuestionStore.clearSearch();
    QuestionStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    QuestionStore.applyOrder(value);
    QuestionStore.resetPage();
    QuestionStore.fetch();
  };

  isOptionChecked = (option: DictionaryItem) =>
    !!this.newQuestions.find(movement => movement.value.id === option.value.id);

  render() {
    const { onClose, options } = this.props;

    return (
      <>
        <Container>
          <Row>
            <TitleContainer>
              <Typography variant="h1" color="textPrimary">
                Add Questions
              </Typography>
              {!!this.newQuestions.length && (
                <SubtitleContainer>
                  <Typography variant="subtitle1" color="textPrimary">
                    {`${this.newQuestions.length} items selected`}
                  </Typography>
                </SubtitleContainer>
              )}
            </TitleContainer>
          </Row>

          <Row>
            <Search
              initValue={QuestionStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={QuestionStore.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <Row>
            {!ContentStudioMovementsStore.isLoading ? (
              <QuestionContainer>
                {options.length ? (
                  <InfiniteScroll
                    dataLength={options.length}
                    hasMore={ContentStudioMovementsStore.hasMoreInfo}
                    next={this.fetchMoreQuestions}
                    height={`calc(100vh - ${FORM_INFORMATION_HEIGHT})`}
                    loader={<ScrollLoader />}
                  >
                    {options.map(option => (
                      <QuestionWrapper
                        key={option.value.id}
                        checked={this.isOptionChecked(option)}
                      >
                        <QuestionInfo>
                          <QuestionText>
                            <TrimmedText lines={1} text={option.label}>
                              <Typography variant="h4">
                                {option.label}
                              </Typography>
                            </TrimmedText>
                          </QuestionText>
                        </QuestionInfo>
                        <Checkbox
                          color="primary"
                          onClick={this.onChangeCheckbox(option)}
                          checked={this.isOptionChecked(option)}
                        />
                      </QuestionWrapper>
                    ))}
                  </InfiniteScroll>
                ) : (
                  <Placeholder
                    height={132}
                    width={132}
                    text={
                      ContentStudioMovementsStore.search
                        ? "No matches found. Please try another search."
                        : "No questions added yet."
                    }
                  />
                )}
              </QuestionContainer>
            ) : (
              <Loader />
            )}
          </Row>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={this.onSaveNewMovement}
              fullWidth
            >
              Save
            </Button>
          </ButtonContainer>
        </Buttons>
      </>
    );
  }
}

export default withGlobalSnackbar(QuestionForm);
